/**
 * Invitation Accept flow — public page.
 *
 * URL: /invite/accept?token=<raw_token>
 *
 * Backend flow:
 *   GET  /api/rbac/invitations/public/verify/{token}  → validates & shows details
 *   POST /api/rbac/invitations/public/accept          → creates staff user
 *
 * Auth model: This platform uses Emergent-managed Google OAuth. There
 * is no password. After acceptance, the invitee must sign in with the
 * SAME Google email address to gain access.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy } from "@phosphor-icons/react";
import api from "../lib/api";
import { teamLoginUrl } from "../lib/appUrls";

export default function InvitationAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, error: null, invite: null });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing invitation token", invite: null });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/rbac/invitations/public/verify/${encodeURIComponent(token)}`);
        setState({ loading: false, error: null, invite: data });
      } catch (e) {
        const msg = e.response?.data?.detail || "Invitation is invalid or expired";
        setState({ loading: false, error: msg, invite: null });
      }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/rbac/invitations/public/accept", { token });
      setAccepted(true);
      toast.success("Invitation accepted. Sign in with Google to continue.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="font-mono text-xs tracking-widest text-zinc-500" data-testid="invite-loading">
          VERIFYING INVITATION…
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <div className="max-w-md w-full border border-red-300 bg-red-50 p-6 rounded-sm" data-testid="invite-error">
          <div className="text-xs font-mono tracking-widest text-red-700 mb-2">INVITATION INVALID</div>
          <div className="text-sm text-zinc-900" data-testid="invite-error-message">{state.error}</div>
          <button
            className="gf-btn gf-btn-outline mt-4"
            onClick={() => navigate("/")}
            data-testid="invite-error-home"
          >
            Return to home
          </button>
        </div>
      </div>
    );
  }

  const inv = state.invite;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-sm p-6 shadow-sm" data-testid="invite-accept">
        <div className="flex items-baseline gap-2 mb-2">
          <div className="text-xl font-bold tracking-tight text-zinc-900">
            GetFuel<span className="text-orange-500">.</span>
          </div>
          <div className="text-xs font-mono tracking-widest text-zinc-500">
            TEAM INVITATION
          </div>
        </div>
        <h1 className="text-xl font-semibold text-zinc-900 mb-1" data-testid="invite-heading">
          {accepted ? "Account activated successfully" : "You're invited to Get Fuel Card"}
        </h1>

        {!accepted && (
          <>
            <p className="text-sm text-zinc-600 mb-4">
              You&apos;ve been invited to join the Get Fuel Card admin team.
            </p>
            <div className="space-y-2 text-sm mb-4">
              <Row label="Email"       value={inv.email} testId="invite-email" />
              <Row label="Name"        value={`${inv.first_name || ""} ${inv.last_name || ""}`.trim() || "—"} testId="invite-name" />
              <Row label="Roles"       value={(inv.assigned_roles || []).map((r) => r.replace(/^role_/, "").replace(/_/g, " ")).join(", ") || "—"} testId="invite-roles" />
              <Row label="Expires at"  value={inv.expires_at ? new Date(inv.expires_at).toLocaleString(undefined, {dateStyle:"medium", timeStyle:"short"}) : "—"} testId="invite-expires" />
            </div>
            <div className="text-xs text-zinc-700 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <strong>Google sign-in required.</strong> This platform uses
              Google authentication — no separate password. After clicking
              <em> Accept Invitation</em>, sign in with your Google
              account using <strong>{inv.email}</strong> to access the
              platform.
            </div>
            <div className="flex gap-2">
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => navigate("/login")}
                data-testid="invite-cancel"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="gf-btn gf-btn-primary flex-1"
                onClick={accept}
                disabled={busy}
                data-testid="invite-accept-button"
              >
                {busy ? "Accepting…" : "Accept Invitation"}
              </button>
            </div>
          </>
        )}

        {accepted && (
          <div data-testid="invite-accepted">
            <div className="text-sm text-zinc-900 mb-2 font-medium">
              Your team account has been activated successfully.
            </div>
            <div className="text-sm text-zinc-700 mb-4">
              Your Get Fuel Card admin account is ready for{" "}
              <strong>{inv.email}</strong>. Sign in with Google using
              that email to reach the admin console.
            </div>

            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 mb-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Permanent Team Login
              </div>
              <div className="text-xs text-zinc-700 mb-2">
                Use this login page for all future access. Your
                invitation link can only be used once.
              </div>
              <div className="font-mono text-xs break-all bg-white border border-zinc-200 rounded-sm p-2"
                   data-testid="permanent-team-login-url">
                {teamLoginUrl()}
              </div>
              <button
                className="gf-btn gf-btn-outline text-xs mt-2"
                data-testid="copy-team-login-link"
                onClick={() => {
                  navigator.clipboard?.writeText(teamLoginUrl());
                  toast.success("Login link copied");
                }}
              >
                <Copy size={12} weight="bold" /> Copy Login Link
              </button>
            </div>

            <button
              className="gf-btn gf-btn-primary w-full"
              onClick={() => navigate("/login")}
              data-testid="invite-goto-login"
            >
              Continue to Sign In with Google
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 border-b border-zinc-100 pb-1">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 truncate" data-testid={testId}>{value}</div>
    </div>
  );
}
